import { isWeekend, isoWeekday, type PlanningBoardDto, shiftDate, type TaskDto, todayInWarsaw } from '@bqcrm/shared';
import { ActionIcon, Center, Group, Loader, Paper, SimpleGrid, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { dayRange } from '../../format';
import { dayLabel } from './common';

const surname = (name: string | null) => (name ?? '').trim().split(/\s+/).at(-1) ?? '';
const mondayOf = (d: string) => shiftDate(d, 1 - isoWeekday(d));

export type DayInfo = { members: string[]; busy: { label: string; time: string }[]; absent: string[] };

/** Zajętość ekipy w danym dniu: skład, zadania (bez planowanego zadania) i nieobecności. */
export function teamDay(board: PlanningBoardDto, teamId: string, day: string, skipTaskId: string): DayInfo {
  const members = board.teams.find((t) => t.id === teamId)?.days.find((d) => d.day === day)?.userIds ?? [];
  const busy: DayInfo['busy'] = [];
  for (const t of board.tasks) {
    if (t.id === skipTaskId || t.status === 'anulowane') continue;
    if (!t.assignments.some((a) => a.day === day && a.state !== 'odwolany' && members.includes(a.userId))) continue;
    const seg = t.segments.find((s) => s.dateFrom <= day && s.dateTo >= day && (s.teamId === teamId || s.added.some((u) => members.includes(u)))) ?? t.segments.find((s) => s.dateFrom <= day && s.dateTo >= day);
    busy.push({ label: surname(t.clientName) || t.title, time: seg?.timeFrom ? `${seg.timeFrom}–${seg.timeTo}` : 'cały dzień' });
  }
  const name = (id: string) => surname(board.people.find((p) => p.id === id)?.fullName ?? '');
  const absent: string[] = [];
  for (const e of board.entries) {
    if (e.dateFrom > day || e.dateTo < day) continue;
    if (e.kind === 'nieobecnosc' && e.absenceStatus !== 'odrzucona') for (const u of e.userIds.filter((u) => members.includes(u))) absent.push(`${name(u)}: ${e.title || 'nieobecność'}`);
    if (e.kind === 'wydarzenie' && (e.allUsers || e.teamIds.includes(teamId) || e.userIds.some((u) => members.includes(u)))) absent.push(e.title || 'wydarzenie');
  }
  return { members, busy, absent };
}

/** Zajętość wybranych pracowników (planowanie bez ekipy): ich zadania (bez planowanego), nieobecności i wydarzenia. */
export function peopleDay(board: PlanningBoardDto, userIds: string[], day: string, skipTaskId: string): DayInfo {
  const name = (id: string) => surname(board.people.find((p) => p.id === id)?.fullName ?? '');
  const many = userIds.length > 1;
  const busy: DayInfo['busy'] = [];
  for (const t of board.tasks) {
    if (t.id === skipTaskId || t.status === 'anulowane') continue;
    const who = [...new Set(t.assignments.filter((a) => a.day === day && a.state !== 'odwolany' && userIds.includes(a.userId)).map((a) => a.userId))];
    if (!who.length) continue;
    const seg = t.segments.find((s) => s.dateFrom <= day && s.dateTo >= day);
    busy.push({ label: `${surname(t.clientName) || t.title}${many ? ` (${who.map(name).join(', ')})` : ''}`, time: seg?.timeFrom ? `${seg.timeFrom}–${seg.timeTo}` : 'cały dzień' });
  }
  // ekipy, do których należą wybrani tego dnia (wydarzenia ekipy dotyczą także ich)
  const teamsOf = board.teams.filter((t) => t.days.some((d) => d.day === day && d.userIds.some((u) => userIds.includes(u)))).map((t) => t.id);
  const absent: string[] = [];
  for (const e of board.entries) {
    if (e.dateFrom > day || e.dateTo < day) continue;
    if (e.kind === 'nieobecnosc' && e.absenceStatus !== 'odrzucona') for (const u of e.userIds.filter((u) => userIds.includes(u))) absent.push(`${name(u)}: ${e.title || 'nieobecność'}`);
    if (e.kind === 'wydarzenie' && (e.allUsers || e.userIds.some((u) => userIds.includes(u)) || e.teamIds.some((t) => teamsOf.includes(t)))) absent.push(e.title || 'wydarzenie');
  }
  return { members: userIds, busy, absent };
}

/** Dzień wolny dla ekipy: ma skład, nikt nie jest zajęty ani nieobecny. */
export const isFree = (i: DayInfo) => i.members.length > 0 && i.busy.length === 0 && i.absent.length === 0;

/**
 * Kalendarz wybranej ekipy albo wybranych pracowników (2 tygodnie) w oknie planowania: zajęcia (klient, godziny),
 * nieobecności, brak składu. Kliknięcie dnia ustawia termin wpisu.
 */
export function TeamCalendar({ task, teamId, userIds = [], anchor, selFrom, selTo, onPick }: { task: TaskDto; teamId: string | null; userIds?: string[]; anchor: string; selFrom: string; selTo: string; onPick: (day: string) => void }) {
  const today = todayInWarsaw();
  const [start, setStart] = useState(() => mondayOf(anchor < today ? today : anchor));
  // wybrany dzień poza widokiem — przesunięcie widoku
  useEffect(() => {
    if (anchor < start || anchor > shiftDate(start, 13)) setStart(mondayOf(anchor));
    // tylko przy zmianie terminu z zewnątrz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);
  const to = shiftDate(start, 13);
  const board = useQuery({ queryKey: ['planning-board', start, to], queryFn: () => api<PlanningBoardDto>(`/planning/board?from=${start}&to=${to}`) });
  const days = Array.from({ length: 14 }, (_, i) => shiftDate(start, i));

  return (
    <Paper withBorder p="xs" radius="md" mt="xs" bg="var(--mantine-color-default-hover)">
      <Group justify="space-between" mb={6}>
        <Text size="xs" fw={600}>
          {teamId ? 'Kalendarz ekipy' : userIds.length > 1 ? 'Kalendarz pracowników' : 'Kalendarz pracownika'} — kliknij dzień, żeby ustawić termin
        </Text>
        <Group gap={2}>
          <ActionIcon size="sm" variant="subtle" onClick={() => setStart(shiftDate(start, -7))} disabled={shiftDate(start, 6) < today} aria-label="Tydzień wcześniej">
            <IconChevronLeft size={14} />
          </ActionIcon>
          <Text size="xs" c="dimmed">
            {dayRange(start, to)}
          </Text>
          <ActionIcon size="sm" variant="subtle" onClick={() => setStart(shiftDate(start, 7))} aria-label="Tydzień później">
            <IconChevronRight size={14} />
          </ActionIcon>
        </Group>
      </Group>
      <SimpleGrid cols={{ base: 2, xs: 4, sm: 7 }} spacing={4} verticalSpacing={4}>
        {days.map((d) => {
          const info = board.data ? (teamId ? teamDay(board.data, teamId, d, task.id) : peopleDay(board.data, userIds, d, task.id)) : null;
          const past = d < today;
          const sel = d >= selFrom && d <= selTo;
          const free = info ? isFree(info) : false;
          // tła przez zmienne motywu (jasny i ciemny wariant)
          const bg = !info ? undefined : isWeekend(d) ? 'var(--app-past)' : !info.members.length ? 'var(--app-subtle)' : free ? 'var(--mantine-color-green-light)' : 'var(--mantine-color-orange-light)';
          return (
            <UnstyledButton
              key={d}
              disabled={past}
              onClick={() => onPick(d)}
              style={{ borderRadius: 6, padding: 6, minHeight: 64, background: bg, opacity: past ? 0.45 : 1, outline: sel ? '2px solid var(--mantine-color-eko-6)' : '1px solid var(--mantine-color-default-border)', cursor: past ? 'default' : 'pointer' }}
            >
              <Stack gap={2}>
                <Text size="xs" fw={700}>
                  {dayLabel(d).wd} {dayLabel(d).dm}
                </Text>
                {info && !info.members.length && (
                  <Text size="10px" c="dimmed">
                    brak składu
                  </Text>
                )}
                {info?.busy.slice(0, 3).map((b, i) => (
                  <Tooltip key={i} label={`${b.label} · ${b.time}`}>
                    <Text size="10px" c="var(--mantine-color-orange-text)" truncate>
                      {b.time === 'cały dzień' ? '■' : b.time} {b.label}
                    </Text>
                  </Tooltip>
                ))}
                {info && info.busy.length > 3 && (
                  <Text size="10px" c="dimmed">
                    +{info.busy.length - 3} więcej
                  </Text>
                )}
                {info?.absent.slice(0, 2).map((a, i) => (
                  <Text key={i} size="10px" c="var(--mantine-color-red-text)" truncate>
                    {a}
                  </Text>
                ))}
                {info && free && !isWeekend(d) && (
                  <Text size="10px" c="var(--mantine-color-green-text)">
                    wolne
                  </Text>
                )}
              </Stack>
            </UnstyledButton>
          );
        })}
      </SimpleGrid>
      {board.isLoading && (
        <Center mt={4}>
          <Loader size="xs" />
        </Center>
      )}
    </Paper>
  );
}
